import { useCallback, useEffect, useRef, useState } from 'react';
import axios from './waistApi';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import WaistForm from './WaistForm';
import { displayWaist, errorMessage, localDateTime } from './waistUtils';

export default function WaistHistory({ userId, admin = false, onUpdated }) {
  const [start, setStart] = useState(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return localDateTime(d).slice(0, 10); });
  const [end, setEnd] = useState(() => localDateTime().slice(0, 10));
  const [range, setRange] = useState({ start, end });
  const [records, setRecords] = useState([]);
  const [unit, setUnit] = useState('cm');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  const base = admin ? '/api/Admin/waist' : '/api/Waist';
  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    setBusy(true); setError('');
    try {
      const res = await axios.get(base, { params: { startDate: range.start || undefined, endDate: range.end || undefined, userId } });
      if (id === requestId.current) setRecords(res.data);
    } catch (err) { if (id === requestId.current) { setRecords([]); setError(errorMessage(err)); } }
    finally { if (id === requestId.current) setBusy(false); }
  }, [base, range, userId]);
  useEffect(() => { refresh(); return () => { requestId.current += 1; }; }, [refresh]);
  const updated = () => { setEditing(null); refresh(); onUpdated?.(); };
  const remove = async (record) => {
    if (!window.confirm('確定要刪除這筆腰圍紀錄？')) return;
    setBusy(true); setError('');
    try { await axios.delete(`${base}/${record.id}`); updated(); }
    catch (err) { setError(errorMessage(err)); setBusy(false); }
  };
  return <div>
    <form className="d-flex flex-wrap gap-2 align-items-end mb-3" onSubmit={e => {
      e.preventDefault();
      if (start && end && start > end) { setError('起始日期不可晚於結束日期。'); return; }
      setEditing(null); setRange({ start, end });
    }}>
      <label>起始日期<input className="form-control" type="date" value={start} onChange={e => setStart(e.target.value)} /></label>
      <label>結束日期<input className="form-control" type="date" value={end} onChange={e => setEnd(e.target.value)} /></label>
      <button className="btn btn-primary" disabled={busy}>查詢</button>
      <label>圖表單位<select className="form-select" value={unit} onChange={e => setUnit(e.target.value)}><option value="cm">公分 (cm)</option><option value="in">英吋 (in)</option></select></label>
    </form>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {busy && <p role="status">處理中…</p>}
    {editing && <div className="card card-body mb-3"><h4>編輯腰圍紀錄</h4><WaistForm key={editing.id} record={editing} admin={admin} onSuccess={updated} onCancel={() => setEditing(null)} /></div>}
    {records.length > 0 && <div style={{ width: '100%', height: 300 }}><ResponsiveContainer>
      <LineChart data={[...records].reverse().map(r => ({ ...r, displayValue: Number(displayWaist(r.waistInCm, unit)) }))}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="recordedAt" tickFormatter={s => s.slice(5, 10)} /><YAxis unit={` ${unit}`} />
        <Tooltip labelFormatter={s => String(s).replace('T', ' ')} /><Line dataKey="displayValue" name={`腰圍 (${unit})`} stroke="#168578" />
      </LineChart>
    </ResponsiveContainer></div>}
    <div className="table-responsive"><table className="table table-bordered table-striped"><thead><tr>
      <th>測量時間</th><th>公分 (cm)</th><th>英吋 (in)</th><th>備註</th><th>建立者</th><th>最後修改者</th><th>操作</th>
    </tr></thead><tbody>
      {!busy && records.length === 0 && <tr><td colSpan={7} className="text-center">尚無資料</td></tr>}
      {records.map(r => <tr key={r.id}><td>{r.recordedAt.replace('T', ' ').slice(0, 16)}</td><td>{displayWaist(r.waistInCm)}</td><td>{displayWaist(r.waistInCm, 'in')}</td><td>{r.notes}</td><td>{r.sourceName}</td><td>{r.updatedByName}</td><td>
        {(admin || r.source === r.userId) && <><button className="btn btn-outline-primary btn-sm me-1" disabled={busy} onClick={() => setEditing(r)}>編輯</button><button className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => remove(r)}>刪除</button></>}
      </td></tr>)}
    </tbody></table></div>
  </div>;
}
